import { ToastMethods } from "components/ToastNotification";
import i18n from "i18n";
import { action, observable } from "mobx";
import type {
  DiscoAdapterSideMenuEndpointResponse,
  EdifyChapterOverview,
} from "models/disco-adapter/EdifyChapter";
import {
  getChapterExtraPractising,
  getChapterOverview,
  getSubchapterTheory,
} from "repositories/DiscoContentRepository";

interface DiscoContentStoreInterface {
  loading: boolean;
  chapterOverview?: EdifyChapterOverview;
  chapterExtraPractising?: unknown;
  subchapterTheory?: unknown;
  error?: Error | null;
  fetchChapterOverview: (packageId: string, chapterId: string) => void;
  fetchChapterExtraPractising: (chapterId: string) => void;
  fetchSubchapterTheory: (packageId: string, chapterId: string, subchapterId: string) => void;
  setLoading: (isLoading: boolean) => void;
  setChapterOverview: (content?: EdifyChapterOverview) => void;
  setChapterExtraPractising: (content: unknown) => void;
  setSubchapterTheory: (content: unknown) => void;
}

const initialState = {
  loading: false,
  chapterOverview: undefined,
  chapterExtraPractising: undefined,
  subchapterTheory: undefined,
  error: null,
};

const stateSetters = {
  setLoading: action((loading: boolean) => {
    store.loading = loading;
  }),
  setChapterOverview: action((content?: EdifyChapterOverview) => {
    store.chapterOverview = content;
  }),
  setChapterExtraPractising: action((content: unknown) => {
    store.chapterExtraPractising = content;
  }),
  setSubchapterTheory: action((content: unknown) => {
    store.subchapterTheory = content;
  }),
};

const apiRequests = {
  fetchChapterOverview: action((packageId: string, chapterId: string) => {
    store.setLoading(true);
    getChapterOverview(packageId, chapterId)
      .then((content: DiscoAdapterSideMenuEndpointResponse) => {
        const [chapterOverview] = content?.chapters || [];
        store.setChapterOverview(chapterOverview);
      })
      .catch(() => {
        ToastMethods.showToast(i18n.t("toast:discoContent.getChapterOverview.error"), "error");
      })
      .finally(() => {
        store.setLoading(false);
      });
  }),
  fetchChapterExtraPractising: action((chapterId: string) => {
    store.setLoading(true);
    getChapterExtraPractising(chapterId)
      .then((content: unknown) => {
        store.setChapterExtraPractising(content);
      })
      .catch(() => {
        ToastMethods.showToast(
          i18n.t("toast:discoContent.getChapterExtraPractising.error"),
          "error",
        );
      })
      .finally(() => {
        store.setLoading(false);
      });
  }),
  fetchSubchapterTheory: action((packageId: string, chapterId: string, subchapterId: string) => {
    store.setLoading(true);
    getSubchapterTheory(packageId, chapterId, subchapterId)
      .then((content: unknown) => {
        store.setSubchapterTheory(content);
      })
      .catch(() => {
        ToastMethods.showToast(i18n.t("toast:discoContent.getSubchapterTheory.error"), "error");
      })
      .finally(() => {
        store.setLoading(false);
      });
  }),
};

const store = observable({
  ...initialState,
  ...stateSetters,
  ...apiRequests,
} as DiscoContentStoreInterface);

export const useDiscoContent = (): DiscoContentStoreInterface => store;
